import { useEffect, useRef } from 'react'
import {
  FiBriefcase,
  FiCode,
  FiMapPin,
  FiMessageCircle,
  FiUserCheck,
} from 'react-icons/fi'

import styles from './Links.module.css'

const whatsappMessage =
  'Olá, João! Vi seu link bio e gostaria de conversar sobre um projeto.'
const whatsappUrl = `https://wa.me/5535999564940?text=${encodeURIComponent(whatsappMessage)}`
const assetVersion = '20260730-2'

const clients = [
  { name: 'Aurora', src: '/clients/em-06.png', width: 48 },
  { name: 'ProPack Uniformes', src: '/clients/propack-uniformes.png', width: 125 },
  { name: 'Recanto do Pescador', src: '/clients/recanto-pescador.png', width: 112 },
  { name: 'Salugi Uniformes', src: '/clients/salugi-uniformes.png', width: 118 },
  { name: 'HR Store', src: '/clients/hr-store.png', width: 48 },
  { name: 'Studio Bella', src: '/clients/studio-bella.png', width: 114 },
  { name: 'Enfermagem IA', src: '/clients/enfermagem-ia.png', width: 120 },
  { name: 'Estilo Certo', src: '/clients/estilo-certo.png', width: 106 },
] as const

const projects = [
  {
    name: 'Plataforma de Carros',
    description: 'Vitrine digital para uma concessionária apresentar e vender veículos.',
    image: '/projects/carros.png',
  },
  {
    name: 'ProPack Uniformes',
    description: 'Site para venda de uniformes personalizados para empresas e equipes.',
    image: '/projects/propac-uniformes.png',
  },
  {
    name: 'Recanto do Pescador',
    description: 'Site para pesqueiro com ranking, gamificação e cardápio digital.',
    image: '/projects/recanto-pescador.png',
  },
  {
    name: 'HR Store',
    description: 'E-commerce de roupas masculinas pensado para facilitar a compra online.',
    image: '/projects/hrstore.png',
  },
  {
    name: 'Salugi Uniformes',
    description: 'Site para apresentação e venda de uniformes personalizados.',
    image: '/projects/salugi-uniformes.png',
  },
  {
    name: 'Aurora',
    description: 'E-commerce de roupas femininas com experiência de compra moderna.',
    image: '/projects/aurora.png',
  },
  {
    name: 'Studio Bella',
    description: 'Agendamento para manicure com escolha de cor e horário online.',
    image: '/projects/studio-bella.png',
  },
] as const

function ClientSequence({ duplicate = false }: { duplicate?: boolean }) {
  return (
    <ul className={styles.clientSequence} aria-hidden={duplicate || undefined}>
      {clients.map((client) => (
        <li key={`${duplicate ? 'copy' : 'original'}-${client.name}`}>
          <img
            src={`${client.src}?v=${assetVersion}`}
            alt={duplicate ? '' : `Logo da ${client.name}`}
            width={client.width}
            height={40}
            loading="lazy"
          />
        </li>
      ))}
    </ul>
  )
}

export default function Links() {
  const projectListRef = useRef<HTMLUListElement>(null)

  useEffect(() => {
    const previousTitle = document.title
    document.title = 'João Victor | Dev — Sites, lojas virtuais e sistemas'
    document.body.classList.add('links-page')

    return () => {
      document.title = previousTitle
      document.body.classList.remove('links-page')
    }
  }, [])

  useEffect(() => {
    const list = projectListRef.current
    if (!list) return

    const cards = Array.from(list.querySelectorAll<HTMLElement>('[data-project-card]'))
    const pendingCards = new Set(cards)
    let animationFrame = 0

    const revealVisibleCards = () => {
      animationFrame = 0
      const listBounds = list.getBoundingClientRect()

      pendingCards.forEach((card) => {
        const cardBounds = card.getBoundingClientRect()
        const visible =
          cardBounds.right > listBounds.left + 24 &&
          cardBounds.left < listBounds.right - 24

        if (visible) {
          pendingCards.delete(card)
          card.classList.add(styles.projectVisible)
        }
      })
    }

    const scheduleReveal = () => {
      if (!animationFrame) {
        animationFrame = window.requestAnimationFrame(revealVisibleCards)
      }
    }

    list.addEventListener('scroll', scheduleReveal, { passive: true })
    window.addEventListener('resize', scheduleReveal, { passive: true })
    scheduleReveal()

    return () => {
      list.removeEventListener('scroll', scheduleReveal)
      window.removeEventListener('resize', scheduleReveal)
      if (animationFrame) window.cancelAnimationFrame(animationFrame)
    }
  }, [])

  return (
    <main className={styles.page}>
      <div className={styles.topGlow} aria-hidden="true" />
      <div className={styles.content}>
        <section className={styles.hero} aria-labelledby="links-title">
          <div className={styles.photoWrap}>
            <img
              src={`/minhafoto.png?v=${assetVersion}`}
              alt="Foto de João Victor | Dev"
              width={118}
              height={118}
              className={styles.photo}
            />
          </div>

          <h1 id="links-title">João Victor | Dev</h1>
          <p className={styles.role}>Desenvolvedor Full-Stack</p>
          <p className={styles.intro}>
            Sites, lojas virtuais e sistemas personalizados com visão real de negócio.
          </p>

          <div className={styles.location}>
            <FiMapPin aria-hidden="true" />
            <span>Campo Belo, MG</span>
            <i aria-hidden="true" />
            <span>Atendimento em todo o Brasil</span>
          </div>
          <p className={styles.availability}>
            <span aria-hidden="true" />
            Disponível para novos projetos
          </p>

          <a
            className={styles.animatedButton}
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FiMessageCircle aria-hidden="true" />
            Falar sobre meu projeto
          </a>
        </section>

        <section className={styles.differentials} aria-label="Diferenciais profissionais">
          <div>
            <FiBriefcase aria-hidden="true" />
            <p>Soluções sob medida<span>para cada negócio</span></p>
          </div>
          <div>
            <FiUserCheck aria-hidden="true" />
            <p>Atendimento<span>direto comigo</span></p>
          </div>
          <div>
            <FiCode aria-hidden="true" />
            <p>Design e desenvolvimento<span>no mesmo projeto</span></p>
          </div>
        </section>

        <section className={styles.clients} aria-labelledby="clients-title">
          <h2 id="clients-title">ALGUNS CLIENTES</h2>
          <div className={styles.marquee} role="region" aria-label="Logos de clientes">
            <div className={styles.marqueeTrack}>
              <ClientSequence />
              <ClientSequence duplicate />
            </div>
          </div>
          <p>
            Algumas marcas com as quais já trabalhei em projetos para moda, saúde,
            fitness e comércio.
          </p>
        </section>

        <section className={styles.projects} aria-labelledby="projects-title">
          <span>PROJETOS</span>
          <h2 id="projects-title">Alguns projetos que já trabalhei</h2>
          <ul ref={projectListRef} className={styles.projectList}>
            {projects.map((project) => (
              <li key={project.name} data-project-card className={styles.projectCard}>
                <article>
                  <img
                    src={`${project.image}?v=${assetVersion}`}
                    alt={`Prévia do projeto ${project.name}`}
                    width={640}
                    height={314}
                    loading="lazy"
                  />
                  <div>
                    <h3>{project.name}</h3>
                    <p>{project.description}</p>
                  </div>
                </article>
              </li>
            ))}
          </ul>
        </section>

        <section className={styles.contact} aria-labelledby="contact-title">
          <div className={styles.particles} aria-hidden="true">
            {Array.from({ length: 7 }, (_, index) => <i key={index} />)}
          </div>
          <h2 id="contact-title">Tem um projeto em mente?</h2>
          <p>Conte sua ideia e receba uma orientação inicial diretamente comigo.</p>
          <a
            className={styles.animatedButton}
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FiMessageCircle aria-hidden="true" />
            Chamar no WhatsApp
          </a>
        </section>

        <footer className={styles.footer}>
          <p>© 2026 João Victor | Dev</p>
          <p>Design, tecnologia e visão de negócio.</p>
        </footer>
      </div>
    </main>
  )
}
